"use client";

import { Link } from "@/i18n/routing";
import { ArrowUpRight, Eye } from "lucide-react";
import { useFormatter, useTranslations } from "next-intl";
import { twMerge } from "tailwind-merge";
import { useIsPro } from "@/components/common/ads/AdsProvider";
import type { ListingViewStats } from "@/lib/types/search";

export default function ListingViews({
  views,
  variant = "card",
}: {
  views: ListingViewStats;
  variant?: "card" | "gallery";
}) {
  const t = useTranslations("listing.views");
  const formatter = useFormatter();
  const isPro = useIsPro();
  const stats = isPro ? views : null;

  const gallery = variant === "gallery";
  const description = stats
    ? `${t("count", { count: stats.count })} · ${t(`period.${stats.window}`)}. ${t("explanation")}`
    : t("unlock");
  const lockedAccessibleLabel = `${t("label")} PRO: ${description}`;
  const className = gallery
    ? "absolute bottom-3 left-3 z-10 inline-flex items-center gap-2 rounded-xl border border-white/25 bg-[#161d14]/95 px-3 py-2 text-white shadow-lg backdrop-blur-md sm:bottom-4 sm:left-4 sm:gap-2.5 sm:px-3.5 sm:py-2.5"
    : "inline-flex h-4 shrink-0 items-center gap-1 whitespace-nowrap text-[10px] leading-4 @lg:h-5 @lg:gap-1.5 @lg:text-[11px]";

  const content = (
    <>
      <Eye
        aria-hidden
        className={
          gallery
            ? "size-5 shrink-0 text-secondary sm:size-6"
            : "size-3 shrink-0 text-muted-foreground @lg:size-3.5"
        }
      />
      {gallery ? (
        <span className="flex flex-col gap-0.5">
          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
            {stats ? (
              <b className="text-base font-semibold sm:text-lg">
                {formatter.number(stats.count)}
              </b>
            ) : (
              <span
                aria-hidden
                className="mr-1 h-2.5 w-5 rounded-sm bg-white/70 blur-[3px]"
              />
            )}
            {t("label")}
            <span className="ml-1 text-[8px] font-semibold tracking-wide text-secondary">
              PRO
            </span>
          </span>
          <span
            className={twMerge(
              "flex items-center gap-1 text-[9px] sm:text-[10px]",
              stats ? "text-white/80" : "font-medium text-secondary",
            )}
          >
            {stats
              ? `${t(`card-period.${stats.window}`)} · ${t("on-luntero")}`
              : t("view-pro")}
            {!stats && <ArrowUpRight aria-hidden className="size-3" />}
          </span>
        </span>
      ) : stats ? (
        <span className="text-zinc-600">
          <b className="font-semibold text-foreground">
            {formatter.number(stats.count)}
          </b>{" "}
          {t("label")}{" "}
          <span aria-hidden className="text-zinc-400">
            ·
          </span>{" "}
          <span className="@lg:hidden">
            {t(`short-period.${stats.window}`)}
          </span>
          <span className="hidden @lg:inline">
            {t(`card-period.${stats.window}`)}
          </span>
        </span>
      ) : (
        <>
          <span className="font-medium text-blue-700">{t("label")}</span>
          <span className="rounded-[3px] bg-secondary px-1 text-[7px] font-semibold leading-3 text-secondary-foreground @lg:text-[8px] @lg:leading-[14px]">
            PRO
          </span>
          <ArrowUpRight aria-hidden className="size-2.5 text-blue-700" />
        </>
      )}
    </>
  );

  return stats ? (
    <span className={className} title={description} aria-label={description}>
      {content}
    </span>
  ) : (
    <Link
      href="/pro#listing-views"
      className={twMerge(
        className,
        "transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2",
      )}
      aria-label={lockedAccessibleLabel}
      title={description}
    >
      {content}
    </Link>
  );
}
